import { useMemo } from "react";
import { Matchs } from "../../../types";
import { MatchStats } from "../types";

function median(arr: number[]): number {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  } else {
    return sorted[mid];
  }
}

function percentile(arr: number[], p: number): number {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, idx)];
}

export function useMatchStats(matches: Matchs[]): MatchStats {
  return useMemo(() => {
    const stats: MatchStats = {
      total: matches.length,
      lancables: 0,
      bloques: 0,
      enCours: 0,
      termines: 0,
      medianCreationToLaunch: 0,
      medianLaunchToEnd: 0,
      p95CreationToLaunch: 0,
      p95LaunchToEnd: 0,
      parTableau: {},
      parNbJoueurs: {},
    };

    const creationToLaunch: number[] = [];
    const launchToEnd: number[] = [];

    matches.forEach((match) => {
      const tableauId = match.tableauId || "inconnu";
      const nbJoueurs = match.matchsPlayerParticipations
        ? match.matchsPlayerParticipations.length
        : 0;
      if (!stats.parTableau[tableauId]) {
        stats.parTableau[tableauId] = {
          total: 0,
          lancables: 0,
          bloques: 0,
          enCours: 0,
          termines: 0,
          medianCreationToLaunch: 0,
          medianLaunchToEnd: 0,
          p95CreationToLaunch: 0,
          p95LaunchToEnd: 0,
          creationToLaunchArr: [],
          launchToEndArr: [],
        };
      }
      const nbJoueursKey = String(nbJoueurs);
      if (!stats.parNbJoueurs[nbJoueursKey]) {
        stats.parNbJoueurs[nbJoueursKey] = {
          count: 0,
          creationToLaunchArr: [],
          launchToEndArr: [],
          medianCreationToLaunch: 0,
          medianLaunchToEnd: 0,
          p95CreationToLaunch: 0,
          p95LaunchToEnd: 0,
        };
      }
      stats.parTableau[tableauId].total++;
      stats.parNbJoueurs[nbJoueursKey].count++;

      switch (match.status) {
        case "CREE":
        case "LIBERE":
          stats.lancables++;
          stats.parTableau[tableauId].lancables++;
          break;
        case "DOUBLON":
          stats.bloques++;
          stats.parTableau[tableauId].bloques++;
          break;
        case "ENCOURS":
          stats.enCours++;
          stats.parTableau[tableauId].enCours++;
          break;
        case "TERMINE":
          stats.termines++;
          stats.parTableau[tableauId].termines++;
          break;
        default:
          break;
      }

      // Durées globales (ignorer si dates non normalisées en Date)
      if (
        match.dateCreation instanceof Date &&
        match.dateLancement instanceof Date
      ) {
        const ms =
          match.dateLancement.getTime() - match.dateCreation.getTime();
        if (ms > 0) {
          creationToLaunch.push(ms / 1000); // en secondes
          stats.parTableau[tableauId].creationToLaunchArr!.push(ms / 1000);
          stats.parNbJoueurs[nbJoueursKey].creationToLaunchArr!.push(ms / 1000);
        }
      }
      if (
        match.dateLancement instanceof Date &&
        match.dateFin instanceof Date
      ) {
        const ms = match.dateFin.getTime() - match.dateLancement.getTime();
        if (ms > 0) {
          launchToEnd.push(ms / 1000); // en secondes
          stats.parTableau[tableauId].launchToEndArr!.push(ms / 1000);
          stats.parNbJoueurs[nbJoueursKey].launchToEndArr!.push(ms / 1000);
        }
      }
    });

    // Médianes et percentiles globaux
    stats.medianCreationToLaunch = median(creationToLaunch);
    stats.medianLaunchToEnd = median(launchToEnd);
    stats.p95CreationToLaunch = percentile(creationToLaunch, 95);
    stats.p95LaunchToEnd = percentile(launchToEnd, 95);

    // Par tableau
    Object.keys(stats.parTableau).forEach((tableauId) => {
      const t = stats.parTableau[tableauId];
      t.total = t.lancables + t.bloques + t.enCours + t.termines;
      t.medianCreationToLaunch = median(t.creationToLaunchArr || []);
      t.medianLaunchToEnd = median(t.launchToEndArr || []);
      t.p95CreationToLaunch = percentile(t.creationToLaunchArr || [], 95);
      t.p95LaunchToEnd = percentile(t.launchToEndArr || [], 95);
      delete t.creationToLaunchArr;
      delete t.launchToEndArr;
    });

    // Par nombre de joueurs
    Object.keys(stats.parNbJoueurs).forEach((nb) => {
      const t = stats.parNbJoueurs[nb];
      t.medianCreationToLaunch = median(t.creationToLaunchArr || []);
      t.medianLaunchToEnd = median(t.launchToEndArr || []);
      t.p95CreationToLaunch = percentile(t.creationToLaunchArr || [], 95);
      t.p95LaunchToEnd = percentile(t.launchToEndArr || [], 95);
      delete t.creationToLaunchArr;
      delete t.launchToEndArr;
    });

    return stats;
  }, [matches]);
}
